/**
 * @gamo/api-client: the typed fetch wrapper every GoGamo client uses to talk
 * to the engine. Bearer token, X-App-Id and the build's version on every call;
 * the error envelope becomes an EngineError; every path, body and reply is
 * read from @gamo/api-types, which is generated from openapi-engine/openapi.yaml.
 * Platform-free: no DOM, no React Native; the token store and fetch are injected.
 *
 * To add a call: the spec first, then `pnpm generate:types`, then a method here
 * that names the path as the spec spells it. src/contract.test.ts fails when a
 * method calls something the spec does not have.
 */
import type { components, paths } from "@gamo/api-types/engine";

export { prettyMath, prettyFigure, mathNodes, type MathNode } from "./math";
export { BLANK_PATTERN, splitBlanks, type BlankPiece } from "./blank";
export { isScore, parseScore, splitTotal, totalOf, type Scale } from "./goal";
export { busiestHours, busiestPart, busiestWeekday, secondsPerQuestion, shareRight, trendOf, wholeMinutes, type DayPart } from "./facts";
export { isBelowVersion } from "./version";
export { aheadOfPace, awaitsPrediction, byType, clockShows, duration, inRange, isAnswered, kindName, lengthLine, lessonCards, mmss, previewLength, resultLine, secondsLeft, setAnswers, setElapsedMs, sizeLine, stemOf, supplyLine, type LengthPreview, type LessonCard, type SetDraft, type TypeRow } from "./strategy";

export type Schemas = components["schemas"];
export type RenderedMove = Schemas["RenderedMove"];
export type LearnerMe = Schemas["LearnerMe"];
export type Course = Schemas["Course"];
export type CourseSummary = Schemas["CourseSummary"];
export type Enrolment = Schemas["Enrolment"];
export type SessionSummary = Schemas["SessionSummary"];
export type AskChat = Schemas["AskChat"];
export type AskExchange = Schemas["AskExchange"];
export type AskTranscript = Schemas["AskTranscript"];
export type TurnRequest = Schemas["TurnRequest"];
/** How sure the learner was before checking an answer (enums.yaml confidence_levels, 2026-09-29): sent with `respond` when the move asked. */
export type ConfidenceLevel = Schemas["ConfidenceLevel"];
export type SessionMode = Schemas["SessionMode"];
export type Move = Schemas["Move"];
export type HelpOption = Schemas["HelpOption"];
export type PremiumOffer = Schemas["PremiumOffer"];
export type AppVersion = paths["/app/version"]["get"]["responses"]["200"]["content"]["application/json"];
export type EnrolRequest = NonNullable<paths["/enrolments"]["post"]["requestBody"]>["content"]["application/json"];
export type ProfilePatch = NonNullable<paths["/learner/profile"]["put"]["requestBody"]>["content"]["application/json"];
export type OutcomeRequest = NonNullable<paths["/outcomes"]["post"]["requestBody"]>["content"]["application/json"];
export type FlagKind = NonNullable<paths["/items/{id}/flag"]["post"]["requestBody"]>["content"]["application/json"]["kind"];
export type Section = Schemas["Section"];
export type Onboarding = Schemas["Onboarding"];
export type GoalView = Schemas["GoalView"];
export type TestResult = Schemas["TestResult"];
export type TestResultRequest = Schemas["TestResultRequest"];
export type TestResultsView = Schemas["TestResultsView"];
export type TestResultSaved = Schemas["TestResultSaved"];
export type TestResultForm = Schemas["TestResultForm"];
export type GoalRequest = Schemas["GoalRequest"];
export type StudentGoal = Schemas["StudentGoal"];
export type PracticeFacts = Schemas["PracticeFacts"];
export type ClassFacts = Schemas["ClassFacts"];
export type LearnerProgress = Schemas["LearnerProgress"];
export type FactsRules = Schemas["FactsRules"];
export type TryTally = Schemas["TryTally"];
export type SectionScores = Schemas["SectionScores"];
export type HelpMode = LearnerMe["help_mode_default"];
export type Focus = Schemas["Focus"];
export type ResolvedFocus = Schemas["ResolvedFocus"];
export type PracticeIntent = Schemas["PracticeIntent"];
export type IntentStart = Schemas["IntentStart"];
export type IntentLine = Schemas["IntentLine"];
export type Reader = Schemas["Reader"];
export type Figure = Schemas["Figure"];
export type SessionTranscript = Schemas["SessionTranscript"];
export type EnrolmentSummary = Schemas["EnrolmentSummary"];
export type MasteryRow = Schemas["MasteryRow"];
export type RecentSession = Schemas["RecentSession"];
// Schools (docs/PLAN-2026-09-20-schools.md).
export type SharingTerms = Schemas["SharingTerms"];
export type SchoolMe = Schemas["SchoolMe"];
export type SchoolClass = Schemas["SchoolClass"];
export type ClassOverview = Schemas["ClassOverview"];
export type SchoolStudentRef = Schemas["SchoolStudentRef"];
export type SchoolStudent = Schemas["SchoolStudent"];
export type SchoolActivity = Schemas["SchoolActivity"];
export type SchoolDailySeries = Schemas["SchoolDailySeries"];
export type SchoolAttention = Schemas["SchoolAttention"];
export type SchoolWeek = Schemas["SchoolWeek"];
export type SchoolFocus = Schemas["SchoolFocus"];
export type SkillStanding = Schemas["SkillStanding"];
export type MissedQuestion = Schemas["MissedQuestion"];
export type SchoolSession = Schemas["SchoolSession"];
export type SchoolTranscript = Schemas["SchoolTranscript"];
/** One move as a school sees it: the allow-listed subset of a RenderedMove (engine org.ts `SCHOOL_MOVE_FIELDS`). */
export type SchoolMove = Schemas["SchoolMove"];
export type ClassPreview = Schemas["ClassPreview"];
export type ClassInvitation = Schemas["ClassInvitation"];
export type AgeAnswer = Schemas["AgeAnswer"];
export type ClassInvite = Schemas["ClassInvite"];
export type MyClass = Schemas["MyClass"];
export type WallpaperTone = Schemas["WallpaperTone"];
export type SchoolPulse = Schemas["SchoolPulse"];

export type SchoolThemeIds = Schemas["SchoolThemeIds"];
export type SchoolThemeColors = Schemas["SchoolThemeColors"];
export type ThemeOptions = Schemas["ThemeOptions"];

/**
 * The school whose logo and colors stand in for GoGamo's in a student's app (founder, 2026-09-21): its name, its logo
 * and the colors it picked, nothing else.
 */
export interface SchoolBrand {
  school_name: string;
  logo_path: string;
  school_theme?: SchoolThemeColors | null;
}

/**
 * Which school brands a student's app: the school of the class they joined most recently, among the classes they are
 * in now and whose school has a logo. None, and the app is GoGamo's as ever. The same rule on the web and the phone.
 * A place the school turned off, or that ended with its class, has no `since` and brands nothing: the student is not in
 * that class now (engine 2026-09-21: /classes/mine lists those places so the app can say so).
 */
export function schoolBrandOf(classes: readonly (Pick<MyClass, "school_name" | "logo_path" | "since"> & Partial<Pick<MyClass, "school_theme" | "status">>)[]): SchoolBrand | null {
  let best: ((typeof classes)[number] & { since: number }) | null = null;
  for (const c of classes) {
    if (c.since === null || (c.status !== undefined && c.status !== "active")) continue;
    if (c.logo_path && (!best || c.since > best.since)) best = c as (typeof classes)[number] & { since: number };
  }
  return best?.logo_path ? { school_name: best.school_name, logo_path: best.logo_path, school_theme: best.school_theme ?? null } : null;
}
export type SchoolRole = Schemas["SchoolRole"];
export type Credentials = Schemas["Credentials"];
export type ResetLink = Schemas["ResetLink"];
export type StaffInvitation = Schemas["StaffInvitation"];
export type SchoolStaff = Schemas["SchoolStaff"];
export type ClassMissed = Schemas["ClassMissed"];
export type SchoolQuestion = Schemas["SchoolQuestion"];
export type SchoolRow = Schemas["SchoolRow"];
export type SchoolDetail = Schemas["SchoolDetail"];
export type SchoolLimits = Schemas["SchoolLimits"];
export type StaffMe = Schemas["StaffMe"];
export type StaffUser = Schemas["StaffUser"];
export type StaffRole = Schemas["StaffRole"];
export type AdminStats = Schemas["AdminStats"];
export type BlueprintSnapshot = Schemas["BlueprintSnapshot"];
export type BlueprintUnit = Schemas["BlueprintUnit"];
export type BlueprintCall = Schemas["BlueprintCall"];
export type BlueprintActivity = Schemas["BlueprintActivity"];
export type BlueprintQuestion = Schemas["BlueprintQuestion"];
export type LabWindow = Schemas["LabWindow"];
export type BookHighlight = Schemas["BookHighlight"];
export type StrategySet = Schemas["StrategySet"];
export type OpenedSet = Schemas["OpenedSet"];
export type SetAnswer = Schemas["SetAnswer"];
export type SetReview = Schemas["SetReview"];
export type StrategyLesson = Schemas["StrategyLesson"];
export type LengthChoice = Schemas["LengthChoice"];
export type SetSupply = Schemas["SetSupply"];
export type SetCheck = Schemas["SetCheck"];
export type SetLevelState = Schemas["SetLevelState"];
export type SetClock = Schemas["SetClock"];
export type LearnerRow = Schemas["LearnerRow"];
export type LearnerDetail = Schemas["LearnerDetail"];
export type AnalyticsInsights = Schemas["AnalyticsInsights"];
/** When practice happens, on each student's own clock (founder, 2026-09-29): Radar's Analytics app and a learner's page. */
export type AnalyticsCell = Schemas["AnalyticsCell"];
export type SessionRow = Schemas["SessionRow"];
export type SessionDetail = Schemas["SessionDetail"];
export type FlagRow = Schemas["FlagRow"];
export type FlagDetail = Schemas["FlagDetail"];
export type Objective = Schemas["Objective"];
// Books (docs/PLAN-2026-09-17-books.md).
export type BookSummary = Schemas["BookSummary"];
export type BookProgress = Schemas["BookProgress"];
export type BookRow = Schemas["BookRow"];
export type BookShelf = Schemas["BookShelf"];
export type BookDetail = Schemas["BookDetail"];
export type BookPart = Schemas["BookPart"];
export type BookContentsEntry = Schemas["BookContentsEntry"];
export type BookChapter = Schemas["BookChapter"];
export type BookBlock = Schemas["BookBlock"];
export type BookEpigraph = Schemas["BookEpigraph"];
export type BookProgressInput = Schemas["BookProgressInput"];
export type ReadingSettings = Schemas["ReadingSettings"];
export type ReadingTheme = Schemas["ReadingTheme"];
export type BookSearch = Schemas["BookSearch"];
export type Bookmark = Schemas["Bookmark"];
export type BookFormatReport = Schemas["BookFormatReport"];
export type BookFormatResult = Schemas["BookFormatResult"];
export type AdminBook = Schemas["AdminBook"];
export type PlacementReport = Schemas["PlacementReport"];
export type PlacementStatus = Schemas["PlacementStatus"];
export type WordCard = Schemas["WordCard"];
export type WordsToday = Schemas["WordsToday"];
// Flash cards (docs/PLAN-2026-09-17-flashcards.md).
export type DeckRequest = Schemas["DeckRequest"];
export type FlashCard = Schemas["FlashCard"];
export type CardGrade = Schemas["CardGrade"];
export type CardsReport = Schemas["CardsReport"];
export type CardDecks = Schemas["CardDecks"];
export type DeckTopic = Schemas["DeckTopic"];
// Guest access (founder, 2026-09-17).
export type GuestState = Schemas["GuestState"];
export type GuestLimits = Schemas["GuestLimits"];
// The product rating, "How are we doing?" (2026-09-17).
export type RatingPrompt = Schemas["RatingPrompt"];
export type RatingText = Schemas["RatingText"];
export type RatingPlatform = Schemas["RatingPlatform"];
export type RatingAnswer = Schemas["RatingAnswer"];
export type RatingState = Schemas["RatingState"];
export type RatingRow = Schemas["RatingRow"];
export type RatingWeek = Schemas["RatingWeek"];
export type AdminRatings = Schemas["AdminRatings"];
// Send feedback (founder, 2026-09-22): plain words to the GoGamo team, read in Radar's Student feedback app.
export type FeedbackStatus = Schemas["FeedbackStatus"];
export type FeedbackRow = Schemas["FeedbackRow"];
export type FeedbackKind = Schemas["FeedbackKind"];
export type AdminFeedback = Schemas["AdminFeedback"];
export type FeedbackRequest = NonNullable<paths["/feedback"]["post"]["requestBody"]>["content"]["application/json"];
// The bank archive (founder, 2026-10-10): Radar's Questions app, sources Staged and Official.
export type StagedStatus = Schemas["StagedStatus"];
export type AdminStagedRow = Schemas["AdminStagedRow"];
export type AdminStaged = Schemas["AdminStaged"];
export type AdminStagedDetail = Schemas["AdminStagedDetail"];
export type ArchiveWord = Schemas["ArchiveWord"];
export type ReferenceNotice = Schemas["ReferenceNotice"];
export type AdminReferenceRow = Schemas["AdminReferenceRow"];
export type AdminReference = Schemas["AdminReference"];
export type AdminReferenceDetail = Schemas["AdminReferenceDetail"];
export type AdminReferencePdfs = Schemas["AdminReferencePdfs"];
type StagedQuery = NonNullable<paths["/admin/staged"]["get"]["parameters"]["query"]>;
type ReferenceQuery = NonNullable<paths["/admin/reference"]["get"]["parameters"]["query"]>;
/** The archive lists' filters; `has_figure` is a boolean here and words on the wire. */
export type AdminStagedFilter = Omit<StagedQuery, "has_figure"> & { has_figure?: boolean };
export type AdminReferenceFilter = Omit<ReferenceQuery, "has_figure"> & { has_figure?: boolean };
export type FeedbackFilter = NonNullable<NonNullable<paths["/admin/feedback"]["get"]["parameters"]["query"]>["status"]>;
// Radar's Questions app (founder, 2026-10-10): the bank's practice questions for review.
export type ItemSource = Schemas["ItemSource"];
export type AdminItemRow = Schemas["AdminItemRow"];
export type AdminItems = Schemas["AdminItems"];
export type AdminItemDetail = Schemas["AdminItemDetail"];
type ItemsQuery = NonNullable<paths["/admin/items"]["get"]["parameters"]["query"]>;
/** The Questions list's filters; `has_figure` is a boolean here and words on the wire. */
export type AdminItemsFilter = Omit<ItemsQuery, "has_figure"> & { has_figure?: boolean };
// The feel pulse, "How does using GoGamo feel right now?" (founder, 2026-09-29): carried on LearnerMe.pulse; answered
// by sendFeedback with surface "pulse" and a reaction (FeedbackRequest already carries the field).
export type PulsePrompt = Schemas["PulsePrompt"];
export type PulseText = Schemas["PulseText"];
export type PulseReaction = Schemas["PulseReaction"];
// The Readiness lab (founder, 2026-09-22): readiness is staff only; calibration against real scores, never applied.
export type ReadinessLab = Schemas["ReadinessLab"];
export type ReadinessHistory = Schemas["ReadinessHistory"];
export type ReadinessSnapshot = Schemas["ReadinessSnapshot"];
export type ScoreOutcome = Schemas["ScoreOutcome"];
export type ScoreRequest = Schemas["ScoreRequest"];
export type LabProposal = Schemas["LabProposal"];
export type LabGainRow = Schemas["LabGainRow"];
export type LabReadinessAt = Schemas["LabReadinessAt"];

// ---- the spec, as types --------------------------------------------------------------------------
// Every call below names its path as the spec spells it and its method, and its body and its reply are read from the
// generated types (contract review, 2026-09-28). A path or a method the spec does not have does not compile, and a
// reply is never typed by hand: when the spec changes, `pnpm generate:types` changes what these methods return.

export type HttpMethod = "get" | "post" | "put" | "patch" | "delete";
/** The paths of the spec that have this method. */
export type PathsWith<M extends HttpMethod> = { [P in keyof paths]: paths[P] extends Record<M, { responses: unknown }> ? P : never }[keyof paths];
type OperationOf<P extends keyof paths, M extends HttpMethod> = paths[P] extends Record<M, infer O> ? O : never;
type JsonOf<T> = T extends { content: { "application/json": infer B } } ? B : never;
type Success = 200 | 201 | 202;
/** What an operation answers when it succeeds, from the spec. */
export type Reply<P extends keyof paths, M extends HttpMethod> = OperationOf<P, M> extends { responses: infer R } ? JsonOf<R[keyof R & Success]> : never;
/** The JSON body an operation takes, from the spec. */
export type Sent<P extends keyof paths, M extends HttpMethod> = OperationOf<P, M> extends { requestBody?: infer B } ? JsonOf<NonNullable<B>> : never;
/** One event of POST /ask/stream. */
export type AskStreamEvent = Schemas["AskStreamEvent"];

export class EngineError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly retryAfterS?: number,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "EngineError";
  }
}

export interface EngineClientOptions {
  baseUrl: string;
  appId: string;
  getToken: () => string | null | Promise<string | null>;
  /**
   * Called when the engine refuses the token itself (401 `auth.required`), so the app can clear it and route to
   * sign-in. Never for a wrong password or a missing app id: those are 401 too, and the session is not at fault.
   */
  onUnauthorized?: () => void;
  inviteCode?: () => string | null;
  /** A school class's code the visitor arrived with; a live one opens sign-up without the tester code. */
  classCode?: () => string | null;
  /** The version this build carries, sent as X-App-Version on every call so the engine can tell versions apart. */
  appVersion?: () => string | null | undefined;
  /** Where the app runs, sent as X-App-Platform beside the version. */
  appPlatform?: () => RatingPlatform | null | undefined;
  timeoutMs?: number;
  fetch?: typeof fetch;
}

export type AuthUser = Schemas["AuthUser"];

/** What signing in gives the app: the token it keeps, and the account. */
export interface SignInResult {
  token: string;
  user: AuthUser;
}

/** What the sign-in screens may offer (GET /auth/methods, 2026-10-09). */
export type AuthMethods = Schemas["AuthMethods"];

/**
 * Why Continue with Google was refused, when it is one of the two answers a screen handles itself (2026-10-09):
 * `no_account`, a Google account no GoGamo account uses yet (better-auth: "signup disabled"), and `password_account`,
 * an email that already has a password account, which Google never takes over (better-auth: "account not linked").
 */
export function googleRefusal(err: unknown): "no_account" | "password_account" | null {
  if (!(err instanceof EngineError)) return null;
  const said = err.message.trim().toLowerCase();
  if (said === "signup disabled") return "no_account";
  if (said === "account not linked") return "password_account";
  return null;
}

/** A guest and its token (guest access, 2026-09-17). The token is kept exactly as a signed-in token is. */
export type GuestResult = Reply<"/guest", "post">;
export type OpenSessionResult = Reply<"/session", "post">;
export type DocumentLink = Reply<"/documents/{id}", "get">;
export type BlueprintAnswerMeta = Extract<Schemas["BlueprintStreamEvent"], { done: unknown }>["done"];

/** One sentence for the person from any failure: the engine's own words, or a plain line. Never a stack trace, never a programmer's message. */
export function describeError(err: unknown): string {
  return err instanceof EngineError ? err.message : "Something went wrong. Try again.";
}

export function newTurnId(): string {
  // Typed by hand: this file is compiled for the browser, React Native and the Worker's tests, and they do not agree
  // on what `globalThis` holds.
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** How many unanswered turns the client remembers: a student has one or two going, never this many. */
const UNANSWERED_KEPT = 32;

interface CallOptions<B> {
  /** The values for the `{holes}` of the path. */
  path?: Record<string, string | number>;
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: B;
  headers?: Record<string, string>;
  /** Send no bearer token: signing in and signing up are done by someone who has no session yet. */
  anonymous?: boolean;
}

export function createEngineClient(options: EngineClientOptions) {
  const f = options.fetch ?? fetch;
  const timeoutMs = options.timeoutMs ?? 45_000;

  const isBlob = (b: unknown): b is Blob => typeof Blob !== "undefined" && b instanceof Blob;

  const queryOf = (values: Record<string, string | number | boolean | null | undefined>) =>
    new URLSearchParams(
      Object.entries(values)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => [k, String(v)]),
    ).toString();

  /** The path as the spec spells it, with its holes filled and its query added. */
  function addressOf(template: string, o: { path?: Record<string, string | number>; query?: Record<string, string | number | boolean | null | undefined> }): string {
    const path = template.replace(/\{([^}]+)\}/g, (_hole, name: string) => {
      const value = o.path?.[name];
      if (value === undefined || value === "") throw new EngineError(0, "client.path_incomplete", `The call to ${template} is missing '${name}'.`);
      return encodeURIComponent(String(value));
    });
    const query = queryOf(o.query ?? {});
    return query ? `${path}?${query}` : path;
  }

  /** The headers every call carries: who (the token), from which app, and which build of it. */
  function identity(token: string | null): Record<string, string> {
    const version = options.appVersion?.();
    const platform = options.appPlatform?.();
    return {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      "x-app-id": options.appId,
      ...(version ? { "x-app-version": version } : {}),
      ...(platform ? { "x-app-platform": platform } : {}),
    };
  }

  /** A refusal as the app reads it, from the engine's envelope or better-auth's own. */
  function refusalOf(status: number, json: unknown, hadToken: boolean): EngineError {
    const env = (json ?? {}) as { code?: string; message?: string; details?: unknown; retry_after_s?: number; requestId?: string };
    // Only the engine's own word that the credential is not accepted ends the session. A wrong password on sign-in is
    // a 401 too (better-auth's), and it must not sign out the guest who mistyped it.
    if (status === 401 && hadToken && env.code === "auth.required") options.onUnauthorized?.();
    // A reply that is not the envelope (a gateway's own page, an empty body) still reaches the person as a sentence.
    const sentence = typeof env.message === "string" && env.message ? env.message : status >= 500 ? "Gamo could not answer just now. Try again in a minute." : "That did not work. Try again.";
    return new EngineError(status, typeof env.code === "string" && env.code ? env.code : `http_${status}`, sentence, env.details, env.retry_after_s, env.requestId);
  }

  async function send(method: string, path: string, body: unknown, extra: Record<string, string>, as: "json" | "text" | "blob", anonymous = false): Promise<unknown> {
    const token = anonymous ? null : await options.getToken();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let res: Response;
    let text: string;
    let bytes: ArrayBuffer | null = null;
    try {
      res = await f(`${options.baseUrl}${path}`, {
        method,
        headers: {
          // A Blob (a school's logo) goes as itself, with its own type; everything else is JSON.
          ...(body === undefined ? {} : { "content-type": isBlob(body) ? body.type : "application/json" }),
          ...identity(token),
          ...extra,
        },
        ...(body === undefined ? {} : { body: isBlob(body) ? body : JSON.stringify(body) }),
        signal: controller.signal,
      });
      // The body is read inside the same clock: a reply whose headers arrived and whose body stalls is still a wait.
      if (as === "blob") {
        bytes = await res.arrayBuffer();
        text = res.ok ? "" : new TextDecoder().decode(bytes);
      } else text = await res.text();
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") throw new EngineError(0, "network.timeout", "Gamo took too long to answer. Check your connection and try again.");
      throw new EngineError(0, "network.unreachable", "Gamo cannot be reached right now. Check your connection.");
    } finally {
      clearTimeout(timer);
    }
    if (res.ok && as === "text") return text;
    if (res.ok && as === "blob") return new Blob([bytes ?? new ArrayBuffer(0)], { type: res.headers.get("content-type") ?? "application/octet-stream" });
    let json: unknown = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }
    if (!res.ok) throw refusalOf(res.status, json, token !== null);
    return json;
  }

  /** One call of the spec: the method, the path as the spec spells it, and what goes in its holes, its query and its body. */
  async function call<M extends HttpMethod, P extends PathsWith<M>>(method: M, template: P, o: CallOptions<Sent<P, M>> = {}): Promise<Reply<P, M>> {
    return (await send(method.toUpperCase(), addressOf(template, o), o.body, o.headers ?? {}, "json", o.anonymous)) as Reply<P, M>;
  }

  /** The same call for a picture: the bytes go as they are, with their own type. */
  async function upload<M extends HttpMethod, P extends PathsWith<M>>(method: M, template: P, o: Omit<CallOptions<never>, "body">, image: Blob): Promise<Reply<P, M>> {
    return (await send(method.toUpperCase(), addressOf(template, o), image, {}, "json")) as Reply<P, M>;
  }

  /** The same call for a reply that is not JSON (the ratings CSV). */
  async function callText<P extends PathsWith<"get">>(template: P, o: CallOptions<never> = {}): Promise<string> {
    return (await send("GET", addressOf(template, o), undefined, {}, "text")) as string;
  }

  /** The same call for a file (a College Board printout for Radar's Official source): the bytes, with their type. */
  async function callBlob<P extends PathsWith<"get">>(template: P, o: CallOptions<never> = {}): Promise<Blob> {
    return (await send("GET", addressOf(template, o), undefined, {}, "blob")) as Blob;
  }

  /**
   * A server-sent stream of JSON events. Each event goes to `onEvent` as it arrives. The clock here is between
   * pieces, not over the whole answer: a long answer that keeps arriving is never cut, and a stream that goes quiet
   * for `timeoutMs` ends as a timeout instead of waiting for good. The caller may stop it (`client.stopped`).
   */
  async function callEvents<P extends PathsWith<"post">, E>(template: P, body: Sent<P, "post">, onEvent: (event: E) => void, signal?: AbortSignal): Promise<void> {
    const token = await options.getToken();
    const controller = new AbortController();
    const abort = (): void => controller.abort();
    if (signal?.aborted) abort();
    signal?.addEventListener("abort", abort);
    let quiet = false;
    let timer = setTimeout(() => ((quiet = true), abort()), timeoutMs);
    const heard = (): void => {
      clearTimeout(timer);
      timer = setTimeout(() => ((quiet = true), abort()), timeoutMs);
    };
    /** Why the stream ended without its last event: it went quiet, the person stopped it, or the connection went. */
    const lost = (err: unknown): EngineError => {
      if (quiet) return new EngineError(0, "network.timeout", "Gamo took too long to answer. Check your connection and try again.");
      if (signal?.aborted || (err instanceof Error && err.name === "AbortError")) return new EngineError(0, "client.stopped", "Stopped.");
      return new EngineError(0, "network.unreachable", "Gamo cannot be reached right now. Check your connection.");
    };
    try {
      let res: Response;
      try {
        res = await f(`${options.baseUrl}${addressOf(template, {})}`, { method: "POST", headers: { "content-type": "application/json", ...identity(token) }, body: JSON.stringify(body), signal: controller.signal });
      } catch (err) {
        throw lost(err);
      }
      if (!res.ok) throw refusalOf(res.status, await res.json().catch(() => null), token !== null);
      // A platform whose fetch cannot read a body as it arrives (React Native's own) has no `body` here.
      if (!res.body) throw new EngineError(0, "client.no_stream", "This app cannot read an answer as it is written.");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        let chunk: ReadableStreamReadResult<Uint8Array>;
        try {
          chunk = await reader.read();
        } catch (err) {
          throw lost(err);
        }
        heard();
        buffer += decoder.decode(chunk.value ?? new Uint8Array(), { stream: !chunk.done });
        const lines = buffer.split(/\r?\n/);
        buffer = chunk.done ? "" : (lines.pop() ?? "");
        for (const line of lines) if (line.startsWith("data:")) onEvent(JSON.parse(line.slice(5)) as E);
        if (chunk.done) return;
      }
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
    }
  }

  /** What the invite gate reads: the tester code and the class code, each only when there is one. */
  const gateHeaders = (): Record<string, string> => {
    const invite = options.inviteCode?.();
    const classCode = options.classCode?.();
    return { ...(invite ? { "x-invite-code": invite } : {}), ...(classCode ? { "x-class-code": classCode } : {}) };
  };

  /**
   * One turn, once (X-1). The engine answers a repeated `turn_id` with the move it stored and writes nothing, so a
   * call that may have reached the engine without its reply reaching the phone (a timeout, a lost connection, a 500)
   * keeps its id, and the same call sent again is the same turn: the answer is not counted twice, the question is not
   * asked twice, the session is not opened twice. A call the engine refused (4xx) wrote nothing and keeps nothing.
   */
  const unanswered = new Map<string, string>();
  async function once<T>(where: string, body: object, given: string | undefined, run: (turnId: string) => Promise<T>): Promise<T> {
    const key = `${where}\n${JSON.stringify(body)}`;
    const turnId = given ?? unanswered.get(key) ?? newTurnId();
    try {
      const reply = await run(turnId);
      unanswered.delete(key);
      return reply;
    } catch (err) {
      if (err instanceof EngineError && (err.status === 0 || err.status >= 500)) {
        unanswered.delete(key);
        unanswered.set(key, turnId);
        for (const oldest of unanswered.keys()) {
          if (unanswered.size <= UNANSWERED_KEPT) break;
          unanswered.delete(oldest);
        }
      } else unanswered.delete(key);
      throw err;
    }
  }

  const openSessionWith = (body: Omit<Sent<"/session", "post">, "turn_id">) => once("/session", body, undefined, (turn_id) => call("post", "/session", { body: { ...body, turn_id } }));
  const turnWith = (sessionId: string, body: Omit<TurnRequest, "turn_id"> & { turn_id?: string }) => {
    const { turn_id: given, ...rest } = body;
    return once(`/session/${sessionId}/turn`, rest, given, (turn_id) => call("post", "/session/{id}/turn", { path: { id: sessionId }, body: { ...rest, turn_id } }));
  };
  const askBody = (text: string, sessionId: string | undefined, fresh: boolean | undefined) => ({ text, ...(sessionId ? { session_id: sessionId } : {}), ...(fresh ? { fresh: true } : {}) });

  return {
    /** A path the engine returns (a school's logo or wallpaper) as a full address on this client's engine. */
    assetUrl: (path: string) => `${options.baseUrl}${path}`,

    // ---- auth (the engine's better-auth routes) ----
    async signUp(email: string, password: string, name: string): Promise<SignInResult> {
      const r = await call("post", "/auth/sign-up/email", { body: { email, password, name }, headers: gateHeaders(), anonymous: true });
      if (r.token) return { token: r.token, user: r.user };
      return this.signIn(email, password);
    },
    async signIn(email: string, password: string): Promise<SignInResult> {
      const r = await call("post", "/auth/sign-in/email", { body: { email, password }, anonymous: true });
      if (!r.token) throw new EngineError(200, "auth.no_session", "Signing in did not open a session. Try again.");
      return { token: r.token, user: r.user };
    },
    /** The ways to sign in a screen may offer (2026-10-09): Google only with a client id, an emailed reset link only when the engine can send one, and the password rule. */
    authMethods: () => call("get", "/auth/methods", { anonymous: true }),
    /**
     * Continue with Google (2026-10-09): the ID token Google's own button handed the page, and the nonce the page gave
     * it. `signUp` asks for a new account when there is none, and then carries the invite code exactly as sign-up does.
     * Without it, a Google account with no GoGamo account is refused (`googleRefusal` reads which way).
     */
    async signInGoogle(idToken: string, nonce: string | null, signUp: boolean): Promise<SignInResult> {
      const body = { provider: "google" as const, idToken: nonce ? { token: idToken, nonce } : { token: idToken }, ...(signUp ? { requestSignUp: true } : {}) };
      const r = await call("post", "/auth/sign-in/social", { body, ...(signUp ? { headers: gateHeaders() } : {}), anonymous: true });
      if (!r.token) throw new EngineError(200, "auth.no_session", "Signing in did not open a session. Try again.");
      return { token: r.token, user: r.user };
    },
    // ---- guest (founder, 2026-09-17: try the app without signing up) ----
    /** Mint a guest. No invite code, no email; the token comes back and is kept like any other. */
    startGuest: (language?: string) => call("post", "/guest", { body: language ? { language } : {}, anonymous: true }),
    /**
     * Turn the guest this client is holding into an account. The engine keeps the
     * same learner row, so the practice, cards and reading carry over. The invite
     * code rides along exactly as it does on sign-up.
     */
    upgradeGuest: (email: string, password: string, name: string) => call("post", "/guest/upgrade", { body: { email, password, name }, headers: gateHeaders() }),
    async signOut(): Promise<void> {
      await call("post", "/auth/sign-out", { body: {} }).catch(() => undefined);
    },

    // ---- learner ----
    me: () => call("get", "/learner/me"),
    updateProfile: (patch: ProfilePatch) => call("put", "/learner/profile", { body: patch }),
    exportMe: () => call("post", "/learner/export"),
    deleteMe: () => call("delete", "/learner/me"),

    // ---- courses ----
    courses: () => call("get", "/courses"),
    course: (code: string) => call("get", "/courses/{code}", { path: { code } }),
    enrol: (body: EnrolRequest) => call("post", "/enrolments", { body }),
    // The published test dates still ahead (onboarding, 2026-09-18), and a date changed later ("Not sure yet" is null).
    examDates: (courseCode: string) => call("get", "/exam-dates", { query: { course_code: courseCode } }),
    setExamDate: (enrolmentId: string, examDate: string | null) => call("put", "/enrolments/{id}/exam-date", { path: { id: enrolmentId }, body: { exam_date: examDate } }),
    // The student's own score goal (2026-09-22): what they scored, what they want, the time they can give, and the estimate.
    goal: (courseCode: string) => call("get", "/learner/goal", { query: { course_code: courseCode } }),
    saveGoal: (body: GoalRequest) => call("put", "/learner/goal", { body }),
    // Bluebook and SAT results (score first, 2026-10-10): numbers only, read on the device; the form and the nudge come back too.
    tests: (courseCode: string) => call("get", "/learner/tests", { query: { course_code: courseCode } }),
    saveTest: (body: TestResultRequest) => call("post", "/learner/tests", { body }),
    fixTest: (id: string, body: TestResultRequest) => call("patch", "/learner/tests/{id}", { path: { id }, body }),
    deleteTest: (id: string) => call("delete", "/learner/tests/{id}", { path: { id } }),
    // Progress in facts (2026-09-22): counts, times and first-try shares from their own practice. No readiness.
    progress: (courseCode: string) => call("get", "/learner/progress", { query: { course_code: courseCode } }),
    // Onboarding's warm-up (2026-09-18): the placement walk, short and mixing the sections.
    openWarmup: (courseCode: string) => openSessionWith({ mode: "placement", course_code: courseCode, placement: "warmup" }),

    // ---- sessions ----
    openSession: (mode: SessionMode, courseCode: string | null, language?: string, drill?: NonNullable<Sent<"/session", "post">["drill"]>, focus?: Focus, deck?: DeckRequest) =>
      openSessionWith({ mode, ...(courseCode ? { course_code: courseCode } : {}), ...(language ? { language } : {}), ...(drill ? { drill } : {}), ...(focus ? { focus } : {}), ...(deck ? { deck } : {}) }),
    /** The Strategies page: every set, with the learner's last result on each. */
    strategies: () => call("get", "/strategies"),
    /** The slides that teach a strategy, with a worked example from the bank. */
    strategyLesson: (kind: string, courseCode: string) => call("get", "/strategies/{kind}/lesson", { path: { kind }, query: { course_code: courseCode } }),
    /** A set taken the test's way: every question at once, graded only on submit. */
    /** A set taken the test's way, as long as the student chose (2026-10-10); with no length, the one they chose last time. */
    /** `level` (2026-10-10): an open level of the strategy; with none, the highest open one. */
    openSet: (kind: string, courseCode: string, section: Section, length?: LengthChoice, level?: number) => call("post", "/strategies/{kind}/open", { path: { kind }, body: { course_code: courseCode, section, ...(length ? { length } : {}), ...(level !== undefined ? { level } : {}) } }),
    /** The length picker's rules, the remembered length, and what the bank can give in each section now, at a level. */
    strategySupply: (kind: string, courseCode: string, level?: number) => call("get", "/strategies/{kind}/supply", { path: { kind }, query: { course_code: courseCode, ...(level !== undefined ? { level } : {}) } }),
    /** Level 1's review (founder decision F2): locks one answer and returns right or wrong, the strategy's line and the explanation. */
    checkSetAnswer: (sessionId: string, answer: SetAnswer) => call("post", "/strategies/sets/{id}/check", { path: { id: sessionId }, body: { answer } }),
    /** A set still open, to pick it up after a closed tab or on another device. */
    setState: (sessionId: string) => call("get", "/strategies/sets/{id}/state", { path: { id: sessionId } }),
    pauseSet: (sessionId: string) => call("post", "/strategies/sets/{id}/pause", { path: { id: sessionId } }),
    resumeSet: (sessionId: string) => call("post", "/strategies/sets/{id}/resume", { path: { id: sessionId } }),
    submitSet: (sessionId: string, answers: SetAnswer[], elapsedMs?: number) => call("post", "/strategies/sets/{id}/submit", { path: { id: sessionId }, body: { answers, ...(elapsedMs !== undefined ? { elapsed_ms: Math.max(0, Math.round(elapsedMs)) } : {}) } }),
    session: (id: string) => call("get", "/session/{id}", { path: { id } }),
    recentSessions: () => call("get", "/sessions/recent"),
    // The whole thread, to pick a session up after leaving it (2026-09-16).
    sessionTranscript: (id: string) => call("get", "/session/{id}/transcript", { path: { id } }),
    // The start screen's words, resolved to a skill or a section, or read as a question (2026-09-16).
    /** One round of the practice start conversation (2026-09-21): what the student typed, with the exchange so far. */
    practiceIntent: (courseCode: string, text: string, exchange: IntentLine[] = []) => call("post", "/practice/intent", { body: { course_code: courseCode, text, ...(exchange.length ? { exchange } : {}) } }),
    setFocus: (enrolmentId: string, patch: Sent<"/enrolments/{id}/focus", "put">) => call("put", "/enrolments/{id}/focus", { path: { id: enrolmentId }, body: patch }),
    // The placement offer declined outside a session (PL6, 2026-09-17): remembered, so the engine never offers twice.
    skipPlacement: (enrolmentId: string) => call("put", "/enrolments/{id}/placement", { path: { id: enrolmentId }, body: { status: "skipped" } }),
    // The Words tab's count (2026-09-17). The cards themselves come through openSession("words", "WORDS-SAT") and turn.
    wordsToday: () => call("get", "/words/today"),
    // Flash cards (docs/PLAN-2026-09-17-flashcards.md): the picker's lists and the register block, a run through openSession("cards", course, ..., deck), the grade through turn({ action: "self_grade" }).
    cards: {
      decks: () => call("get", "/cards/decks"),
      best: (deckKind: string, deckKey?: string | null) => call("get", "/cards/best", { query: { deck: deckKey ? `${deckKind}:${deckKey}` : deckKind } }),
      open: (courseCode: string, deck: DeckRequest) => openSessionWith({ mode: "cards", course_code: courseCode, deck }),
      grade: (sessionId: string, itemId: string | undefined, grade: string, seconds: number) => turnWith(sessionId, { action: "self_grade", ...(itemId ? { item_id: itemId } : {}), grade, seconds }),
    },
    turn: turnWith,
    ask: (text: string, sessionId?: string, language?: string, o: { fresh?: boolean } = {}) => {
      const body = { ...askBody(text, sessionId, o.fresh), ...(language ? { language } : {}) };
      return once("/ask", body, undefined, (turn_id) => call("post", "/ask", { body: { ...body, turn_id } }));
    },
    /**
     * The same exchange, streamed (2026-09-19): `onText` gets each checked piece as Gamo writes it, and the stored
     * move comes back at the end, to replace the streamed text in the chat. Throws the engine's error as `ask` does.
     * Needs a `fetch` that reads a body as it arrives: a browser's, or `expo/fetch` on the phone.
     */
    async askStream(text: string, onText: (piece: string) => void, sessionId?: string, o: { fresh?: boolean; signal?: AbortSignal } = {}): Promise<RenderedMove> {
      const body = askBody(text, sessionId, o.fresh);
      return once("/ask", body, undefined, async (turn_id) => {
        let move: RenderedMove | null = null;
        let failure: Extract<AskStreamEvent, { error: unknown }>["error"] | null = null;
        await callEvents<"/ask/stream", AskStreamEvent>(
          "/ask/stream",
          { ...body, turn_id },
          (e) => {
            if ("text" in e) onText(e.text);
            else if ("done" in e) move = e.done;
            else if ("error" in e) failure = e.error;
          },
          o.signal,
        );
        const failed = failure as Extract<AskStreamEvent, { error: unknown }>["error"] | null;
        // A refusal inside the stream carries no status of its own: the engine's own failure keeps the turn's id, a refusal does not.
        if (failed) throw new EngineError(failed.code === "internal_error" ? 500 : 200, failed.code, failed.message, undefined, failed.retry_after_s);
        if (!move) throw new EngineError(0, "stream.incomplete", "The answer stopped before it finished. Ask again.");
        return move;
      });
    },
    // Past chats (founder, 2026-09-14): list them, read one to resume it. Both answer their newest page; when the
    // reply says `more`, `before` (the oldest row's time) reads the page before it.
    asks: (before?: number) => call("get", "/asks", before === undefined ? undefined : { query: { before } }),
    askTranscript: (sessionId: string, before?: number) => call("get", "/asks/{id}", { path: { id: sessionId }, ...(before === undefined ? {} : { query: { before } }) }),
    deleteAsk: (sessionId: string) => call("delete", "/asks/{id}", { path: { id: sessionId } }),
    /** "Report a mistake" on an Ask reply (D10, 2026-10-01): one anonymous count by model and language, nothing else. */
    reportAskReply: (model: string | null | undefined, language: string) => call("post", "/ask-reports", { body: { ...(model ? { model } : {}), language } }),

    /** Choose a new password. Also how a temporary one ends: until then every authenticated route answers `auth.password_change_required`. Every session ends with it, this one included: keep the `token` that comes back in place of the old one. */
    changePassword: (currentPassword: string, newPassword: string) => call("post", "/account/password", { body: { current_password: currentPassword, new_password: newPassword } }),

    /** Use a password reset link (founder, 2026-09-21). No sign-in: the token is the credential, it is one use, and every session the account had ends. */
    useResetLink: (token: string, newPassword: string) => call("post", "/account/reset", { body: { token, new_password: newPassword }, anonymous: true }),

    // ---- classes: the student's side of a school (schools plan, 2026-09-20) ----
    classes: {
      /** What a class link shows before sign-up: the school's name and logo and the course. No account needed. */
      invitation: (code: string) => call("get", "/classes/invitation", { query: { code } }),
      preview: (code: string) => call("get", "/classes/preview", { query: { code } }),
      join: (code: string, shownName: string) => call("post", "/classes/join", { body: { code, shown_name: shownName } }),
      mine: () => call("get", "/classes/mine"),
      acceptInvite: (inviteId: string, shownName: string) => call("post", "/classes/invites/{id}/accept", { path: { id: inviteId }, body: { shown_name: shownName } }),
      leave: (classId: string) => call("post", "/classes/{id}/leave", { path: { id: classId } }),
    },

    // ---- school: a school admin's classes and students. Every student read is scoped by the engine; see the `school` tag. ----
    school: {
      me: () => call("get", "/org/me"),
      /** This account agrees to join a school that attached it, or says no (founder, 2026-09-21). */
      acceptInvitation: (orgId: string) => call("post", "/org/invitations/{org}/accept", { path: { org: orgId } }),
      declineInvitation: (orgId: string) => call("post", "/org/invitations/{org}/decline", { path: { org: orgId } }),
      /** The dashboard's welcome is finished or skipped, for this person at this school. */
      welcomed: (orgId: string) => call("post", "/org/schools/{org}/welcomed", { path: { org: orgId } }),
      /** The main admin sets the school's logo (PNG, JPEG or WebP). Nobody can be invited until there is one. */
      setLogo: (orgId: string, image: Blob) => upload("put", "/org/schools/{org}/logo", { path: { org: orgId } }, image),
      /** The main admin sets the staff wallpaper, with its tone (light or dark) measured in the browser. Students never see it. */
      setWallpaper: (orgId: string, image: Blob, tone: WallpaperTone) => upload("put", "/org/schools/{org}/wallpaper", { path: { org: orgId }, query: { tone } }, image),
      /** The main admin sets the colors this school's students see. Ids from the register; all null is GoGamo's own. */
      setSchoolTheme: (orgId: string, theme: SchoolThemeIds) => call("put", "/org/schools/{org}/school-theme", { path: { org: orgId }, body: theme }),
      /** The main admin removes the staff wallpaper; the dashboard goes back to its default. */
      removeWallpaper: (orgId: string) => call("delete", "/org/schools/{org}/wallpaper", { path: { org: orgId } }),
      createClass: (orgId: string, body: Sent<"/org/schools/{org}/classes", "post">) => call("post", "/org/schools/{org}/classes", { path: { org: orgId }, body }),
      /** The questions this class misses most. Students are counted, never named. */
      mostMissed: (classId: string) => call("get", "/org/classes/{id}/missed", { path: { id: classId } }),
      /** The phone home screen's numbers for the classes this person sees: practiced today, needing attention, the latest practice. */
      pulse: (orgId: string) => call("get", "/org/schools/{org}/pulse", { path: { org: orgId } }),
      // A school's staff, for its admins. A new account's temporary password comes back once, in `credentials`, and never again.
      staff: {
        list: (orgId: string) => call("get", "/org/schools/{org}/staff", { path: { org: orgId } }),
        /** An account that existed before is invited, not added: the answer is `{ invited: true, credentials: null }` and says nothing about the person. */
        add: (orgId: string, body: Sent<"/org/schools/{org}/staff", "post">) => call("post", "/org/schools/{org}/staff", { path: { org: orgId }, body }),
        update: (orgId: string, userId: string, patch: Sent<"/org/schools/{org}/staff/{user}", "patch">) => call("patch", "/org/schools/{org}/staff/{user}", { path: { org: orgId, user: userId }, body: patch }),
        /** A password reset LINK, shown once (founder, 2026-09-21). For every active member, an account that existed before the school included. */
        resetLink: (orgId: string, userId: string) => call("post", "/org/schools/{org}/staff/{user}/reset-link", { path: { org: orgId, user: userId } }),
        remove: (orgId: string, userId: string) => call("delete", "/org/schools/{org}/staff/{user}", { path: { org: orgId, user: userId } }),
      },
      class: (classId: string) => call("get", "/org/classes/{id}", { path: { id: classId } }),
      updateClass: (classId: string, patch: Sent<"/org/classes/{id}", "patch">) => call("patch", "/org/classes/{id}", { path: { id: classId }, body: patch }),
      student: (classId: string, studentId: string) => call("get", "/org/classes/{id}/students/{student}", { path: { id: classId, student: studentId } }),
      /** Turn a student's place off, or back on. Off, the class code will not let them back in; only the school can. */
      setStudentActive: (classId: string, studentId: string, active: boolean) => call("patch", "/org/classes/{id}/students/{student}", { path: { id: classId, student: studentId }, body: { active } }),
      missed: (classId: string, studentId: string) => call("get", "/org/classes/{id}/students/{student}/missed", { path: { id: classId, student: studentId } }),
      sessions: (classId: string, studentId: string, before?: number) => call("get", "/org/classes/{id}/students/{student}/sessions", { path: { id: classId, student: studentId }, query: { before: before || undefined } }),
      transcript: (classId: string, studentId: string, sessionId: string) => call("get", "/org/classes/{id}/students/{student}/sessions/{session}", { path: { id: classId, student: studentId, session: sessionId } }),
    },

    // ---- items, outcomes, documents ----
    /** "Was this helpful?" under an explanation: one tap on the move that carried it. */
    rateExplanation: (decisionId: string, helpful: boolean, itemId?: string) => call("post", "/explanations/{decision_id}/helpful", { path: { decision_id: decisionId }, body: { helpful, ...(itemId ? { item_id: itemId } : {}) } }),
    flag: (itemId: string, kind: FlagKind, note?: string) => call("post", "/items/{id}/flag", { path: { id: itemId }, body: { kind, ...(note ? { note } : {}) } }),
    outcome: (body: OutcomeRequest) => call("post", "/outcomes", { body }),
    document: (id: string) => call("get", "/documents/{id}", { path: { id } }),

    // ---- the product rating, "How are we doing?" (2026-09-17) ----
    // The card arrives on a move as `rating_prompt`; these record what the learner did with it.
    ratings: {
      answer: (ratingId: string, body: RatingAnswer) => call("post", "/ratings/{id}", { path: { id: ratingId }, body }),
      comment: (ratingId: string, comment: string) => call("post", "/ratings/{id}/comment", { path: { id: ratingId }, body: { comment } }),
    },

    // ---- Send feedback (founder, 2026-09-22): to the GoGamo team, never to a school ----
    sendFeedback: (body: FeedbackRequest) => call("post", "/feedback", { body }),

    // ---- Books (docs/PLAN-2026-09-17-books.md) ----
    books: {
      shelf: () => call("get", "/books"),
      book: (n: number) => call("get", "/books/{n}", { path: { n } }),
      chapter: (n: number, c: number) => call("get", "/books/{n}/chapters/{c}", { path: { n, c } }),
      progress: (n: number, body: BookProgressInput) => call("put", "/books/{n}/progress", { path: { n }, body }),
      /** Highlights in the reading view: the learner's own, for the whole book at once. */
      highlights: (n: number) => call("get", "/books/{n}/highlights", { path: { n } }),
      addHighlight: (n: number, body: Sent<"/books/{n}/highlights", "put">) => call("put", "/books/{n}/highlights", { path: { n }, body }),
      recolorHighlight: (n: number, id: string, color: string) => call("patch", "/books/{n}/highlights/{id}", { path: { n, id }, body: { color } }),
      removeHighlight: (n: number, id: string) => call("delete", "/books/{n}/highlights/{id}", { path: { n, id } }),
      /** The heart: mark a book as a favorite, or take the mark off. */
      setFavorite: (n: number, on: boolean) => (on ? call("put", "/books/{n}/favorite", { path: { n } }) : call("delete", "/books/{n}/favorite", { path: { n } })),
      // The reading view's search and bookmarks (founder, 2026-09-17).
      search: (n: number, q: string) => call("get", "/books/{n}/search", { path: { n }, query: { q } }),
      bookmarks: (n: number) => call("get", "/books/{n}/bookmarks", { path: { n } }),
      addBookmark: (n: number, body: Sent<"/books/{n}/bookmarks", "put">) => call("put", "/books/{n}/bookmarks", { path: { n }, body }),
      removeBookmark: (n: number, id: string) => call("delete", "/books/{n}/bookmarks/{id}", { path: { n, id } }),
    },

    health: () => call("get", "/health"),
    // The mobile app's version floor and store links (forced-update check, registers/app_versions.yaml).
    appVersion: () => call("get", "/app/version"),

    // ---- Radar, the staff workspace (docs/PLAN-2026-09-17-admin-engine.md) ----
    staffExists: (email: string) => call("post", "/auth/staff-exists", { body: { email }, anonymous: true }),
    admin: {
      me: () => call("get", "/admin/me"),
      staff: () => call("get", "/admin/staff"),
      createStaff: (body: Sent<"/admin/staff", "post">) => call("post", "/admin/staff", { body }),
      setStaffRole: (id: string, role: StaffRole | "learner") => call("patch", "/admin/staff/{id}", { path: { id }, body: { role } }),
      stats: () => call("get", "/admin/stats"),
      // Radar's engine Analytics app (permission engine.analytics.view, owner only): ported from Drive's, backed
      // entirely by the engine's own rows.
      // Real students only by default (founder, 2026-09-29); includeTest counts test accounts and guests too.
      analytics: (days = 30, includeTest = false) => call("get", "/admin/analytics", { query: includeTest ? { days, include_test: true } : { days } }),
      // Radar's Schools app (permission engine.orgs.manage, owner only). About the school: no student is listed.
      orgs: {
        list: (opts: { q?: string; before?: number } = {}) => call("get", "/admin/orgs", { query: opts }),
        create: (body: Sent<"/admin/orgs", "post">) => call("post", "/admin/orgs", { body }),
        get: (id: string) => call("get", "/admin/orgs/{id}", { path: { id } }),
        update: (id: string, patch: Sent<"/admin/orgs/{id}", "patch">) => call("patch", "/admin/orgs/{id}", { path: { id }, body: patch }),
        remove: (id: string, typedName: string) => call("delete", "/admin/orgs/{id}", { path: { id }, body: { name: typedName } }),
        replaceMainAdmin: (id: string, body: Sent<"/admin/orgs/{id}/main-admin", "put">) => call("put", "/admin/orgs/{id}/main-admin", { path: { id }, body }),
        /** The founder's reset link, for any member of the school including the main admin. */
        resetLink: (id: string, userId: string) => call("post", "/admin/orgs/{id}/staff/{user}/reset-link", { path: { id, user: userId } }),
        removeStaff: (id: string, userId: string) => call("delete", "/admin/orgs/{id}/staff/{user}", { path: { id, user: userId } }),
      },
      // The Blueprint (docs/PLAN-2026-09-19-engine-blueprint.md): the engine's own description, and how often each rule fired.
      blueprint: () => call("get", "/admin/blueprint"),
      /** Streams the answer: `onText` for each piece; resolves with the model and cost, or throws the engine's error. */
      async askBlueprint(question: BlueprintQuestion, onText: (piece: string) => void, signal?: AbortSignal): Promise<BlueprintAnswerMeta> {
        type Event = Schemas["BlueprintStreamEvent"];
        let meta: BlueprintAnswerMeta | null = null;
        let failure: Extract<Event, { error: unknown }>["error"] | null = null;
        await callEvents<"/admin/blueprint/ask", Event>(
          "/admin/blueprint/ask",
          question,
          (e) => {
            if ("text" in e) onText(e.text);
            else if ("done" in e) meta = e.done;
            else if ("error" in e) failure = e.error;
          },
          signal,
        );
        const failed = failure as Extract<Event, { error: unknown }>["error"] | null;
        if (failed) throw new EngineError(200, failed.code, failed.message);
        if (!meta) throw new EngineError(0, "stream.incomplete", "The answer stopped before it finished. Ask again.");
        return meta;
      },
      /** The Lab's window: recorded decisions with the policy's inputs. Everything, one learner's, or one session's. */
      labDecisions: (opts: { limit?: number; learner_id?: string; session_id?: string } = {}) => call("get", "/admin/blueprint/decisions", { query: opts }),
      blueprintActivity: (days = 7) => call("get", "/admin/blueprint/activity", { query: { days } }),
      learners: (opts: { q?: string; filter?: string; limit?: number } = {}) => call("get", "/admin/learners", { query: opts }),
      learner: (id: string) => call("get", "/admin/learners/{id}", { path: { id } }),
      exportLearner: (id: string) => call("post", "/admin/learners/{id}/export", { path: { id } }),
      deleteLearner: (id: string) => call("delete", "/admin/learners/{id}", { path: { id } }),
      /** Mark or unmark a learner as a test account (engine.learners.mark_test): left out of every count of real students. */
      setLearnerTest: (id: string, isTest: boolean) => call("put", "/admin/learners/{id}/test", { path: { id }, body: { is_test: isTest } }),
      /** Who is behind an analytics id, or the analytics id of an email: one audited action, for support or safety only (data minimization addition C). */
      reidentify: (body: { analytics_id?: string; email?: string; reason: "support_request" | "safety"; note: string }) => call("post", "/admin/learners/reidentify", { body }),
      sessions: (opts: { mode?: string; learner_id?: string; ended?: string; before?: number; limit?: number } = {}) => call("get", "/admin/sessions", { query: opts }),
      session: (id: string) => call("get", "/admin/sessions/{id}", { path: { id } }),
      flags: (opts: { reviewed?: "waiting" | "done" | "all"; kind?: string; label?: string; limit?: number } = {}) => call("get", "/admin/flags", { query: opts }),
      flag: (id: string) => call("get", "/admin/flags/{id}", { path: { id } }),
      reviewFlag: (id: string, note?: string) => call("post", "/admin/flags/{id}/reviewed", { path: { id }, body: note ? { note } : {} }),
      // Radar's Questions app (2026-10-10): the bank's practice questions, newest first, and one in full.
      items: ({ has_figure, ...opts }: AdminItemsFilter = {}) => call("get", "/admin/items", { query: { ...opts, ...(has_figure === undefined ? {} : { has_figure: has_figure ? ("true" as const) : ("false" as const) }) } }),
      item: (id: string) => call("get", "/admin/items/{id}", { path: { id } }),
      retireItem: (id: string, reason?: string) => call("post", "/admin/items/{id}/retire", { path: { id }, body: reason ? { reason } : {} }),
      // The bank archive (founder, 2026-10-10; engine.bank.archive, admin and owner): candidates that never reached the
      // live bank (Staged) and College Board's question bank printouts for staff study (Official).
      staged: ({ has_figure, ...opts }: AdminStagedFilter = {}) => call("get", "/admin/staged", { query: { ...opts, ...(has_figure === undefined ? {} : { has_figure: has_figure ? ("true" as const) : ("false" as const) }) } }),
      stagedItem: (id: string) => call("get", "/admin/staged/{id}", { path: { id } }),
      reference: ({ has_figure, ...opts }: AdminReferenceFilter = {}) => call("get", "/admin/reference", { query: { ...opts, ...(has_figure === undefined ? {} : { has_figure: has_figure ? ("true" as const) : ("false" as const) }) } }),
      referenceItem: (id: string) => call("get", "/admin/reference/{id}", { path: { id } }),
      referencePdfs: () => call("get", "/admin/reference/pdfs"),
      referencePdf: (name: string) => callBlob("/admin/reference/pdfs/{name}", { path: { name } }),
      // The Ratings app (2026-09-17): weekly totals and recent ratings; the export is totals only, as CSV text.
      ratings: (opts: { app?: RatingPlatform; from?: string; to?: string; before?: number; limit?: number } = {}) => call("get", "/admin/ratings", { query: opts }),
      ratingsCsv: (opts: { app?: RatingPlatform; from?: string; to?: string } = {}) => callText("/admin/ratings/export", { query: opts }),
      // Student feedback (2026-09-22): the inbox, newest first, and marking a message read or done with a note.
      feedback: (opts: { status?: FeedbackFilter; kind?: FeedbackKind; surface?: string; q?: string; before?: string; limit?: number } = {}) => call("get", "/admin/feedback", { query: opts }),
      updateFeedback: (id: string, body: Sent<"/admin/feedback/{id}", "patch">) => call("patch", "/admin/feedback/{id}", { path: { id }, body }),
      // The Readiness lab (2026-09-22): calibration for a course, one learner's history, and a score reported to staff.
      readinessLab: (courseCode?: string) => call("get", "/admin/readiness", { query: { course_code: courseCode } }),
      readinessHistory: (learnerId: string, courseCode?: string) => call("get", "/admin/readiness/learners/{id}", { path: { id: learnerId }, query: { course_code: courseCode } }),
      recordScore: (learnerId: string, body: ScoreRequest) => call("post", "/admin/readiness/learners/{id}/scores", { path: { id: learnerId }, body }),
      // Books (plan §4): the list with state and reports, run the pipeline, publish or unpublish.
      books: () => call("get", "/admin/books"),
      formatBook: (n: number) => call("post", "/admin/books/{n}/format", { path: { n } }),
      setBookPublished: (n: number, published: boolean) => call("patch", "/admin/books/{n}", { path: { n }, body: { published } }),
    },
  };
}

export type EngineClient = ReturnType<typeof createEngineClient>;
